import {
  type ClassModuleDetailViewModel,
  useGetApiV1ClassroomdisciplineClassModulesId,
} from '@/shared/api/generated';

/** Envelope `{ data }` que o backend pode usar para respostas de item único. */
interface ClassModuleDetailEnvelope {
  readonly data?: ClassModuleDetailViewModel | null;
}

/**
 * Extrai o detalhe do módulo tolerando os dois formatos de resposta: o objeto
 * achatado declarado no spec e o envelope `{ data }` que o backend usa em runtime
 * (mesmo padrão observado na listagem, cujo spec também declara o formato achatado).
 */
function unwrapDetail(
  raw: ClassModuleDetailViewModel | ClassModuleDetailEnvelope | undefined,
): ClassModuleDetailViewModel | undefined {
  if (!raw) return undefined;
  const looksFlat = 'id' in raw || 'description' in raw || 'dateStart' in raw;
  if (!looksFlat && 'data' in raw) {
    return (raw as ClassModuleDetailEnvelope).data ?? undefined;
  }
  return raw as ClassModuleDetailViewModel;
}

/**
 * Fronteira de dados do detalhe do módulo. Envolve o hook gerado pelo Kubb e só
 * dispara a requisição quando há `id` (modo edição). No modo cadastro (`id`
 * indefinido) permanece desabilitado, sem loading nem erro.
 *
 * @param id - `id` do módulo em edição; `undefined` no cadastro.
 * @returns `{ data, isLoading, isError, refetch }`.
 */
export function useClassModuleDetail(id?: number) {
  const enabled = id !== undefined;

  const { data, isLoading, isError, refetch } = useGetApiV1ClassroomdisciplineClassModulesId(
    id ?? 0,
    { query: { enabled } },
  );

  return {
    data: unwrapDetail(data as ClassModuleDetailViewModel | ClassModuleDetailEnvelope | undefined),
    isLoading: enabled && isLoading,
    isError: enabled && isError,
    refetch,
  } as const;
}
